import { connect, ConnectedProps } from 'react-redux';
import { notification, Tooltip } from 'antd';
import routes from '@root/routes';
import { useTranslation, Trans } from 'react-i18next';

import { Button, CustomIcon, AddToSavedItemsButton } from '@components/shared';
import actions from '@store/actions/listingPage';
import searchPageActions from '@store/actions/listingSearchPage';
import saveActions from '@store/actions/saved';
import useToggle from '@shared/useToggle';
import {
  hasCurrentUser as hasCurrentUserSelector,
  getCurrentUser,
  isListingSaved,
} from '@store/selectors';
import { Listing, StoreState } from '@root/types';
import ShareModal from '../ShareModal';
import TourbookButtonAndOverlay from './Tourbooks';
import styles from './ActionButtons.module.less';

type Props = {
  listing: Listing;
};

const mapStateToProps = (state: StoreState, ownProps: Props) => ({
  isSaved: isListingSaved(state, ownProps.listing.id),
  hasCurrentUser: hasCurrentUserSelector(state),
  currentUser: getCurrentUser(state),
});

const mapDispatchToProps = {
  toggleSaveListing: ({ listing, willBeSaved, onSuccess }) =>
    saveActions.toggleSaveListing({ listing, willBeSaved, onSuccess }),
  openShareModalAction: () => actions.openShareModal(),
  savedListingLinkClicked: () => searchPageActions.savedListingNotificationLinkClicked(),
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

export function RawActionButtons({
  listing,
  isSaved,
  toggleSaveListing,
  hasCurrentUser,
  openShareModalAction,
  savedListingLinkClicked,
  currentUser,
}: Props & ReduxProps) {
  const { t } = useTranslation('listing');

  const {
    value: shareModalIsOpen,
    setTrue: openShareModal,
    setFalse: closeShareModal,
  } = useToggle();

  if (!hasCurrentUser) return null;

  const displaySuccessfulNotification = () => {
    notification.open({
      message: t('listingSavedToFavorites'),
      className: styles.notification,
      /* eslint-disable react/jsx-no-literals */
      description: (
        <Trans i18nKey="viewInFavorites" ns="listing">
          View it in your
          <a href={routes.savedListings} onClick={savedListingLinkClicked}>
            saved listings
          </a>
          .
        </Trans>
      ),
      /* eslint-enable react/jsx-no-literals */
      placement: 'bottomLeft',
      duration: 3.5,
      icon: <CustomIcon type="heart" className={styles.heartIcon} />,
      closeIcon: <CustomIcon type="close" className={styles.closeIcon} />,
    });
  };

  const onSuccess = isSaved ? () => undefined : displaySuccessfulNotification;

  return (
    <div className={styles.container}>
      <TourbookButtonAndOverlay hasCurrentUser={hasCurrentUser} listing={listing} />
      <Tooltip trigger={['hover', 'click']} placement="bottom" title={t('share.ctaText')}>
        <span>
          <Button
            className={styles.shareButton}
            type="tertiary"
            size="small"
            onClick={() => {
              openShareModalAction();
              openShareModal();
            }}
            focus={shareModalIsOpen}
            icon="share"
            data-testid="shareListingButton"
          >
            <span className={styles.buttonText}>{t('share.buttonText')}</span>
          </Button>
        </span>
      </Tooltip>
      <Tooltip trigger={['hover', 'click']} placement="bottom" title={t('saveTooltip')}>
        <span>
          <AddToSavedItemsButton
            className={styles.heartButton}
            textClassName={styles.buttonText}
            data-testid="saveListingButton"
            isSaved={isSaved}
            onClick={() => toggleSaveListing({ listing, willBeSaved: !isSaved, onSuccess })}
          />
        </span>
      </Tooltip>
      <ShareModal
        isOpen={shareModalIsOpen}
        close={closeShareModal}
        listingId={listing.id}
        buildingName={listing.buildingName}
        currentUser={currentUser!}
        neighborhood={listing.building.neighborhood as string}
      />
    </div>
  );
}

export default connector(RawActionButtons);
